/* eslint-disable react/jsx-no-bind */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import { pointerOutsideOfPreview } from '@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview';
import { attachClosestEdge, extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import * as colors from '@constants/colors';
import CloseIcon from '@material-ui/icons/Close';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import './details-layout-drawer.scss';

const TaskTypeRow = ({ taskType, index, onDelete, selectedTaskType, setSelectedTaskType }) => {
  const [hovered, setHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isDraggedOver, setIsDraggedOver] = useState(false);
  const [closestEdge, setClosestEdge] = useState(null);
  const rowRef = useRef(null);
  const dragHandleRef = useRef(null);

  const hoverOn = () => setHovered(true);
  const hoverOff = () => setHovered(false);
  const iconProps = { htmlColor: '#616161', style: { fontSize: '1.25rem', padding: '0 0 0 0.5rem' } };
  const isSelected = taskType.id === selectedTaskType.id;

  // Set up draggable
  useEffect(() => {
    const element = rowRef.current;
    const dragHandle = dragHandleRef.current;

    if (!element || !dragHandle) {
      return () => {};
    }

    return draggable({
      element,
      dragHandle,
      getInitialData: () => ({ index, taskType, type: 'workflow-task-type' }),
      onGenerateDragPreview: ({ nativeSetDragImage }) => {
        setCustomNativeDragPreview({
          nativeSetDragImage,
          getOffset: pointerOutsideOfPreview({ x: '16px', y: '8px' }),  // eslint-disable-line id-length
          render: ({ container }) => {
            const preview = document.createElement('div');
            preview.style.cssText = `
              padding: 0.75rem 1.5rem;
              background-color: white;
              border: 1px solid ${colors.dotmapsGrayBorder};
              border-radius: 4px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.15);
              font-size: 0.875rem;
              font-family: Roboto, sans-serif;
            `;
            preview.textContent = taskType.name;
            container.appendChild(preview);
            return () => container.removeChild(preview);
          }
        });
      },
      onDragStart: () => setIsDragging(true),
      onDrop: () => setIsDragging(false)
    });
  }, [index, taskType]);

  // Set up drop target on each row
  useEffect(() => {
    const element = rowRef.current;
    if (!element) {
      return () => {};
    }

    return dropTargetForElements({
      element,
      canDrop: ({ source }) => source.data.type === 'workflow-task-type',
      getData: ({ input, element: el }) => {
        return attachClosestEdge({ index }, {
          input,
          element: el,
          allowedEdges: ['top', 'bottom']
        });
      },
      onDragEnter: ({ self }) => {
        setIsDraggedOver(true);
        setClosestEdge(extractClosestEdge(self.data));
      },
      onDrag: ({ self }) => {
        setClosestEdge(extractClosestEdge(self.data));
      },
      onDragLeave: () => {
        setIsDraggedOver(false);
        setClosestEdge(null);
      },
      onDrop: () => {
        setIsDraggedOver(false);
        setClosestEdge(null);
      }
    });
  }, [index]);

  return (
    <div
      ref={rowRef}
      onMouseEnter={hoverOn}
      onMouseLeave={hoverOff}
      onClick={() => setSelectedTaskType(taskType)}
      role="presentation"
      styleName={`section-row row row-clickable field ${isSelected ? 'field-active' : ''}`}
      style={{
        border: isDragging ? `1px solid ${colors.dotmapsGrayBorder}` : 'none',
        opacity: isDragging ? 0.4 : 1,
        width: '100%',
        position: 'relative'
      }}
    >
      {/* Drop indicator line */}
      {isDraggedOver && closestEdge === 'top' && (
        <div style={{
          position: 'absolute',
          top: -1,
          left: 0,
          right: 0,
          height: '2px',
          backgroundColor: colors.dotmapsBlue,
          zIndex: 1
        }} />
      )}
      {isDraggedOver && closestEdge === 'bottom' && (
        <div style={{
          position: 'absolute',
          bottom: -1,
          left: 0,
          right: 0,
          height: '2px',
          backgroundColor: colors.dotmapsBlue,
          zIndex: 1
        }} />
      )}
      <div styleName="row-left field-container">
        <div
          ref={dragHandleRef}
          data-testid="drag-handle"
          styleName="drag-handle"
          style={{ cursor: 'grab' }}
        >
          <DragIndicatorIcon htmlColor="#999999" style={{ fontSize: '1rem', width: '1.5rem' }} />
        </div>
        <div styleName={`field-label ${isSelected ? 'field-label-active' : ''}`}>
          {taskType.name}
        </div>
      </div>
      <div styleName="row-right second-text">
        {hovered && (
          <div styleName="row-action">
            <CloseIcon {...iconProps} onClick={() => onDelete(taskType)} />
          </div>
        )}
      </div>
    </div>
  );
};

TaskTypeRow.propTypes = {
  index: PropTypes.number,
  onDelete: PropTypes.func,
  selectedTaskType: PropTypes.object,
  setSelectedTaskType: PropTypes.func,
  taskType: PropTypes.object
};

export default TaskTypeRow;
